<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import type { Option } from '@/storage/products'
import { getOptionValueNameById } from '@/utils/product/getOptionValueNameById'
import ButtonSelectOptionValueHappy from './ButtonSelectOptionValueHappy.vue'
import { computed, nextTick, ref, watch } from 'vue'
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'
import { openStatesHappy } from '@/happyTheme/event/product/openStatesHappy'
import { incrementRadioHappy } from '@/happyTheme/utils/product/optionValue/incrementRadioHappy'
import { productBus } from '@/event/product/productBus'
import { formatPrice } from '@/event/locale'

const props = defineProps<{
    name_option: string
    options: Option
    index: number
}>()

const counterOptionValue = ref(0)

function useIncrementRadionHappy(optionValueId: number) {
    const option = props.options
    if (!option || !option.values || option.values.length === 0) {
        return
    }

    incrementRadioHappy(optionValueId, option, counterOptionValue)
}

const maxOptionCount = computed(() => {
    if (!props.options || !props.options.values) return 0
    const propsOption = props.options

    const resetOption = propsOption.values
        .filter((o) => o.reset == 1)
        .map((o) => o.option_value_id)

    let totalResult = 0

    if (
        (propsOption.free_option_count === 0 && propsOption.max_option_count === 0) ||
        (propsOption.free_option_count !== 0 && propsOption.max_option_count === 0)
    ) {
        totalResult = propsOption.values.length - resetOption.length
    }
    if (propsOption.max_option_count !== 0) {
        totalResult = propsOption.max_option_count
    }

    return totalResult
})

const hasSelectedCheckbox = computed(() => {
    const optionId = props.options.option_id

    if (
        !selectOptionsListHappy.value[optionId]?.selected_values &&
        !selectOptionsListHappy.value[optionId]?.selected_values?.checkbox_option
    ) {
        return props.options.values.map((o) => o.option_value_id)
    }
    const checkboxSelected =
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option

    if (!checkboxSelected) return []

    const optionProps = props.options
    const informationProps = optionProps.values.map((o) => o.option_value_id)

    return informationProps
})

const getCurrentQuantity = (optionValueId: number): number => {
    const optionValue = props.options.values.find(
        (v) => v.option_value_id === optionValueId
    )
    const optionData = selectOptionsListHappy.value[props.options.option_id]
    if (!optionValue || !optionData || !optionData.selected_values?.checkbox_option)
        return 0

    const allSelected = [
        ...optionData.selected_values.checkbox_option.option_value_free,
        ...optionData.selected_values.checkbox_option.option_value_paid
    ]

    const hasGroupReset = allSelected.some(
        (v) => v.option_value_reset === 1 && v.option_value_grup === optionValue.grup
    )

    if (hasGroupReset) return 0

    const count = allSelected.filter((v) => v.option_value_id === optionValueId).length

    return count
}

function getSelectedValuesPaid(optionValueId: number) {
    const value = selectOptionsListHappy.value[
        props.options.option_id
    ]?.selected_values.checkbox_option.option_value_paid.find(
        (o) => o.option_value_id === optionValueId
    )
    const selectValuePaid = value
        ? {
              id: value.option_value_id,
              price: Number(value.option_value_price).toFixed(2)
          }
        : undefined
    return selectValuePaid
}

function isSelected(optionValueId: number): boolean {
    const optionId = props.options.option_id
    const selectedValues = selectOptionsListHappy.value[optionId]?.selected_values
    if (!selectedValues) return false

    if (props.options.type === 'radio') {
        return selectedValues.radio_option.option_value_id === optionValueId
    } else if (props.options.type === 'select' || props.options.type === 'checkbox') {
        const isSelected =
            selectedValues.checkbox_option.option_value_free.some((o) => {
                return o.option_value_id === optionValueId
            }) ||
            selectedValues.checkbox_option.option_value_paid.some((o) => {
                return o.option_value_id === optionValueId
            })
        return isSelected
    }

    return false
}

watch(
    () => selectOptionsListHappy.value[props.options.option_id]?.selected_values,
    async (newValue, oldValue) => {
        if (!newValue) return
        const propsOption = props.options
        const propsOptionMaxCount = propsOption.max_option_count
        const propsOptionFreeCount = propsOption.free_option_count

        if (newValue.radio_option && newValue.radio_option.option_value_id !== 0) {
            openStatesHappy.value[props.index] = false
            if (
                props.index + 1 < openStatesHappy.value.length &&
                openStatesHappy.value[props.index + 1] !== undefined &&
                openStatesHappy.value[props.index + 1] === false
            ) {
                await nextTick()
                setTimeout(() => {
                    openStatesHappy.value[props.index + 1] = true
                }, 100)
            }
        } else {
            const selectCount =
                newValue.checkbox_option.option_value_free.length +
                newValue.checkbox_option.option_value_paid.length
            const optionResetCount = props.options.values.filter(
                (o) => o.reset == 1
            ).length
            const optionSelectedIsPossible =
                props.options.values.length - optionResetCount

            const optionResetSelected =
                newValue.checkbox_option.option_value_free.filter(
                    (o) => o.option_value_reset == 1
                ).length +
                newValue.checkbox_option.option_value_paid.filter(
                    (o) => o.option_value_reset == 1
                ).length

            if (
                (props.options &&
                    propsOptionFreeCount !== 0 &&
                    propsOptionMaxCount === 0) ||
                (propsOptionFreeCount === 0 && propsOptionMaxCount === 0) ||
                (propsOptionFreeCount !== 0 && propsOptionMaxCount !== 0)
            ) {
                if (
                    optionResetCount !== 0 &&
                    optionResetCount === optionResetSelected &&
                    openStatesHappy.value[props.index] === true
                ) {
                    openStatesHappy.value[props.index] = false
                    if (
                        props.index + 1 < openStatesHappy.value.length &&
                        openStatesHappy.value[props.index + 1] !== undefined &&
                        openStatesHappy.value[props.index + 1] === false
                    ) {
                        await nextTick()
                        setTimeout(() => {
                            openStatesHappy.value[props.index + 1] = true
                        }, 100)
                    }
                    return
                }

                if (
                    selectCount === optionSelectedIsPossible &&
                    openStatesHappy.value[props.index] === true
                ) {
                    openStatesHappy.value[props.index] = false
                    if (
                        props.index + 1 < openStatesHappy.value.length &&
                        openStatesHappy.value[props.index + 1] !== undefined &&
                        openStatesHappy.value[props.index + 1] === false
                    ) {
                        await nextTick()
                        setTimeout(() => {
                            openStatesHappy.value[props.index + 1] = true
                        }, 100)
                    }
                }
            }

            if (
                props.options &&
                propsOptionFreeCount !== 0 &&
                propsOptionMaxCount !== 0 &&
                selectCount === maxOptionCount.value
            ) {
                openStatesHappy.value[props.index] = false
                if (
                    props.index + 1 < openStatesHappy.value.length &&
                    openStatesHappy.value[props.index + 1] !== undefined &&
                    openStatesHappy.value[props.index + 1] === false
                ) {
                    await nextTick()
                    setTimeout(() => {
                        openStatesHappy.value[props.index + 1] = true
                    }, 100)
                }
            }

            if (
                props.options &&
                propsOptionFreeCount === 0 &&
                propsOptionMaxCount !== 0 &&
                newValue.checkbox_option.option_value_paid.length === maxOptionCount.value
            ) {
                openStatesHappy.value[props.index] = false
                if (
                    props.index + 1 < openStatesHappy.value.length &&
                    openStatesHappy.value[props.index + 1] !== undefined &&
                    openStatesHappy.value[props.index + 1] === false
                ) {
                    await nextTick()
                    setTimeout(() => {
                        openStatesHappy.value[props.index + 1] = true
                    }, 100)
                }
            }
        }
    },
    { deep: true }
)
</script>

<template>
    <section class="section-option-value-happy">
        <div
            class="div-option-value-happy"
            v-if="props.options"
            v-for="(option_value, index) in props.options.values"
            :class="{
                'border-b': index !== props.options.values.length - 1,
                'selected-option-value-happy': isSelected(option_value.option_value_id),
                'selected-option-value-radio-happy': props.options.type === 'radio',
                'is-last': index === props.options.values.length - 1
            }"
            :key="option_value.option_value_id"
        >
            <div
                class="div-option-value-description-happy"
                @click="
                    props.options.type === 'radio'
                        ? useIncrementRadionHappy(option_value.option_value_id)
                        : null
                "
            >
                <div class="div-info-option-value-happy">
                    <img
                        v-if="option_value.image_type === 0"
                        loading="lazy"
                        class="img-option-value-happy"
                        :src="`${URL_IMAGE}${option_value.image}`"
                        alt=""
                    />
                    <img
                        v-if="option_value.image_type === 1 && productBus?.image"
                        loading="lazy"
                        class="img-option-value-happy"
                        :src="`${URL_IMAGE}${productBus.image}`"
                        alt=""
                    />
                    <div
                        v-if="option_value.image_type === 2 && productBus?.image"
                        class="relative"
                    >
                        <img
                            loading="lazy"
                            class="img-option-value-happy"
                            :src="`${URL_IMAGE}${productBus.image}`"
                            alt=""
                        />
                        <img
                            loading="lazy"
                            class="img-option-value-type-happy"
                            :src="`${URL_IMAGE}${option_value.image}`"
                            alt=""
                        />
                    </div>
                    <span> - </span>
                    <div class="price-container">
                        <p class="text-option-value-happy">
                            {{
                                getOptionValueNameById(
                                    option_value.option_value_id,
                                    languageIdBus
                                )
                            }}
                        </p>

                        <p
                            class="price-select-component-happy"
                            v-if="
                                props.options.type === 'radio' &&
                                parseFloat(option_value.ov_price).toFixed(2) !== '0.00'
                            "
                            :class="{
                                'is-selected': isSelected(option_value.option_value_id)
                            }"
                        >
                            ({{ formatPrice(option_value.ov_price) }})
                        </p>

                        <p
                            class="whitespace-nowrap"
                            :class="{
                                'is-selected': isSelected(option_value.option_value_id)
                            }"
                            v-if="
                                (props.options.type === 'checkbox' ||
                                    props.options.type === 'select') &&
                                getSelectedValuesPaid(option_value.option_value_id) &&
                                parseFloat(
                                    getSelectedValuesPaid(option_value.option_value_id)
                                        ?.price || '0'
                                ).toFixed(2) !== '0.00'
                            "
                        >
                            ({{
                                formatPrice(
                                    getSelectedValuesPaid(option_value.option_value_id)
                                        ?.price || '0'
                                )
                            }})
                        </p>
                    </div>
                </div>
                <div class="button-container">
                    <ButtonSelectOptionValueHappy
                        v-if="
                            hasSelectedCheckbox.includes(option_value.option_value_id) &&
                            props.options.type === 'select'
                        "
                        :key="`select-${option_value.option_value_id}`"
                        :current-option-value-id="option_value.option_value_id"
                        :option="props.options"
                        :max-quantity="maxOptionCount"
                        :current-quantity="
                            getCurrentQuantity(option_value.option_value_id)
                        "
                    />

                    <ButtonSelectOptionValueHappy
                        v-if="
                            hasSelectedCheckbox.includes(option_value.option_value_id) &&
                            props.options.type === 'checkbox'
                        "
                        :key="`checkbox-${option_value.option_value_id}`"
                        :current-option-value-id="option_value.option_value_id"
                        :option="props.options"
                        :max-quantity="maxOptionCount"
                        :current-quantity="
                            getCurrentQuantity(option_value.option_value_id)
                        "
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.section-option-value-happy {
    @apply flex flex-col;
    @apply happy-ifo-dark:gap-0 happy-ifo-dark:py-1 happy-ifo-dark:px-1;
}
.div-option-value-happy {
    /* refactored */
    @apply flex items-center min-w-full gap-2 border-t-border;
    @apply happy-ifo-dark:transition-colors happy-ifo-dark:duration-200 happy-ifo-dark:py-0.5 happy-ifo-dark:px-2 happy-ifo-dark:rounded-md happy-ifo-dark:border-0;
    @apply happy-ifo-dark:border-t-accent/20;
}

.selected-option-value-happy {
    /* refactored */
    @apply bg-t-accent/20 text-t-text;
    @apply happy-ifo-dark:bg-t-accent/15 happy-ifo-dark:text-t-accent happy-ifo-dark:ring-1 happy-ifo-dark:ring-t-accent/40 happy-ifo-dark:my-1;
}

.selected-option-value-radio-happy {
    @apply cursor-pointer;
}

.is-last {
    @apply rounded-b-[0.6rem];
}

.div-option-value-description-happy {
    @apply flex items-center min-w-full justify-between px-2;
}

.div-info-option-value-happy {
    @apply flex items-center gap-2 w-full min-h-12;
}

.img-option-value-happy {
    @apply min-w-12 max-w-12 min-h-12 max-h-12 object-contain;
    @apply happy-ifo-dark:min-w-9 happy-ifo-dark:max-w-9 happy-ifo-dark:min-h-9 happy-ifo-dark:max-h-9 happy-ifo-dark:rounded-full happy-ifo-dark:ring-1 happy-ifo-dark:ring-t-accent/20 happy-ifo-dark:p-0.5 happy-ifo-dark:brightness-105;
}
.img-option-value-type-happy {
    @apply min-w-7 max-w-7 min-h-7 max-h-7 object-contain absolute bottom-1 right-0;
}
.text-option-value-happy {
    @apply text-wrap w-full;
}

.price-select-component-happy {
    /* refactored */
    @apply whitespace-nowrap text-t-accent;
}

.selected-option-value-radio-happy:active,
.selected-option-value-radio-happy:focus {
    /* refactored */
    @apply bg-t-accent/15 text-t-text;
    @apply happy-ifo-dark:bg-t-accent/15 happy-ifo-dark:text-t-accent happy-ifo-dark:ring-1 happy-ifo-dark:ring-t-accent/40 happy-ifo-dark:my-1;
}

.selected-option-value-radio-happy:active .price-select-component-happy,
.selected-option-value-radio-happy:focus .price-select-component-happy {
    /* refactored */
    @apply text-t-accent;
}

.is-selected {
    /* refactored */
    @apply font-bold text-t-accent;
}

.button-container {
    @apply flex items-center shrink-0;
}
.price-container {
    @apply flex items-center flex-wrap;
}

@media (hover: hover) {
    .selected-option-value-radio-happy:hover {
        /* refactored */
        @apply bg-t-accent/15 text-t-text;
        @apply happy-ifo-dark:bg-t-accent/10 happy-ifo-dark:text-t-accent;
    }
    .selected-option-value-radio-happy:hover .price-select-component-happy {
        /* refactored */
        @apply text-t-accent;
    }
}
</style>
